/**
 * RESPONSIBILITY: draw one event, on its own page.
 *
 * The header is CONSTANT: breadcrumb, title, picture, tags and when it happens are always
 * there and always first, because they are what the page is and not something an editor
 * chose to put on it. Everything below is the stack the editor built.
 *
 * The page theme decides the frame and nothing else. Where it provides a sidebar, the
 * second stack is drawn in it; where it does not, that stack follows the first — never
 * hidden, so changing the frame cannot lose an editor's work.
 *
 * Reached by address, so a link keeps working after somebody reorders the list. An
 * address that matches nothing gets a page that says so rather than a blank screen: old
 * links outlive the rows they pointed at, and that is not the visitor's fault.
 */
import type { EventRow, EventsSection } from '@app/sections';
import { PAGE_THEME } from '@app/sections';
import { Picture } from '../commons/Picture';
import { Crumbs } from '../commons/Crumbs';
import { Page, hrefFor } from '../commons/pages';
import { whenText } from '../commons/when';
import { useWords } from '../commons/words';
import { EventStack } from './EventStack';
import { EventInfoPanel } from './EventInfoPanel';
import { EventBanner } from './EventBanner';
import { EventTags } from './EventTags';
import { PlaceMark } from '../commons/PlaceMark';

function Missing({ c }: { c: EventsSection }) {
  const w = useWords();
  return (
    <main>
      <Crumbs trail={[{ label: w.eventsLabel, page: Page.Events }, { label: w.notFoundLabel }]} />
      <section className="band band-centre">
        <div className="wrap">
          <h2>{c.missingHeading}</h2>
          <p className="lede">{c.missingBody}</p>
          <div className="btns btns-centre">
            <a className="btn" href={hrefFor(Page.Events)}>{c.missingCtaLabel}</a>
          </div>
        </div>
      </section>
    </main>
  );
}

/** Always present, always first, never one of the sections. */
function EventHead({ e }: { e: EventRow }) {
  return (
    <header className="ev-head">
      <EventBanner e={e} />
      <div className="ev-head-words">
        <p className="meta">{whenText(e)}</p>
        <h2 className="ev-title">{e.title}</h2>
        {e.summary ? <p className="lede">{e.summary}</p> : null}
        {e.location ? <p className="ev-where"><PlaceMark />{e.location}</p> : null}
        <EventTags e={e} />
      </div>
    </header>
  );
}

export function EventDetail(
  { c, e, all = [] }: { c: EventsSection; e: EventRow | undefined; all?: readonly EventRow[] },
) {
  const w = useWords();
  if (!e) return <Missing c={c} />;

  const railed = e.pageTheme === PAGE_THEME.Rail && (e.railSections?.length ?? 0) > 0;
  const main = <EventStack sections={e.sections} event={e} all={all} />;
  const rail = <EventStack sections={e.railSections} event={e} all={all} />;

  return (
    <main>
      <Crumbs trail={[{ label: w.eventsLabel, page: Page.Events }, { label: e.title }]} />
      <div className="titlebar"><div className="wrap"><h1>{e.title}</h1></div></div>

      <div className={`wrap ev-page${railed ? ' ev-page-railed' : ''}`}>
        <div className="ev-col">
          <EventHead e={e} />
          {/* The practical details go in the sidebar where the page theme gives one, and
              directly under the head where it does not — the same rule the second stack
              follows, so changing the frame never loses them. */}
          {railed ? null : <EventInfoPanel e={e} c={c} />}
          {main}
          {railed ? null : rail}
        </div>
        {railed ? (
          <aside className="ev-side">
            <EventInfoPanel e={e} c={c} />
            {rail}
          </aside>
        ) : null}
      </div>
    </main>
  );
}
